import { Check, Megaphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createReleaseSync } from './releaseSync.js';

// Release protocol of system.miglioresoftware.com (BestPractice guides 24 and 30): the bundle
// knows its own version; /version.json (static, no-store) says which version is published. When
// they differ every open tab reloads by itself, with no confirmation; the notes of the new version
// are shown after the reload, until the user closes them. Checks run on events, never on a timer.

export const APP_VERSION = __APP_VERSION__;

export interface ReleaseArea {
  area: string;
  items: string[];
}

export interface VersionDocument {
  schemaVersion: '1.0' | '1.1';
  applicationId: string;
  version: string;
  publishedAt: string;
  commit?: string;
  title: string;
  areas?: ReleaseArea[];
}

export interface ReleaseNotesDocument {
  schemaVersion: '1.0' | '1.1';
  applicationId: string;
  releases: Array<{ version: string; publishedAt: string; commit?: string; summary: string; areas?: ReleaseArea[] }>;
}

export function ReleaseAreas({ areas }: { areas: ReleaseArea[] }) {
  return (
    <>
      {areas.map((group, index) => (
        <div key={group.area || index} className="release-group">
          {group.area ? <p className="release-area">{group.area}</p> : null}
          <ul>
            {group.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

export function ReleaseUpdateGuard() {
  const [release, setRelease] = useState<VersionDocument>();
  const [sync] = useState(() =>
    createReleaseSync({
      appId: 'monitor',
      bundleVersion: APP_VERSION,
      onNotesChange: (notes) => setRelease(notes as unknown as VersionDocument | undefined),
    }),
  );

  useEffect(() => {
    setRelease(sync.notes() as unknown as VersionDocument | undefined);
    return sync.start();
  }, [sync]);

  if (!release) return null;

  const dismiss = () => {
    sync.dismissNotes();
    setRelease(undefined);
  };

  return createPortal(
    <div className="backdrop">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="release-title">
        <div className="dialog-head">
          <span className="brand-mark">
            <Megaphone size={15} />
          </span>
          <div>
            <div className="label">Aggiornato alla versione {release.version}</div>
            <h2 id="release-title">{release.title}</h2>
          </div>
        </div>
        <div className="dialog-body">
          <ReleaseAreas areas={release.areas ?? []} />
        </div>
        <div className="dialog-foot">
          <button type="button" className="btn primary" onClick={dismiss} autoFocus>
            <Check size={15} />
            Ho capito
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
